import {ChangeEvent, FC, FocusEventHandler, FocusEvent, useRef, useState, useEffect} from "react";

import { Box, useMultiStyleConfig } from "@chakra-ui/react";

import { FormHint } from "@components/common";
import { AutoComplete, AutoCompleteItem, AutoCompleteList } from "@lib/choc-autocomplete";

import NoSuggestions from "./NoSuggestions";
import SelectInput from "./SelectInput";
import { TSelectProps } from "./types";

const getKey = (value: string, i: number): string => `${value}-${i}`;
const FixedOptionSelect: FC<TSelectProps> = ({
  noSuggestionsMessage,
  id,
  name,
  value,
  options,
  isReadOnlySelection,
  isNotClearable,
  hint,
  setFieldValue,
  onChange,
  onBlur,
  onFocus,
  shouldClearOnSelect,
  searchWithOnlyBeginnings,
  ...rest
}) => {
  const currentLabel = options?.find(({ value: optionValue }) => optionValue === value)?.label ?? "";
  const labelRef = useRef(currentLabel);
  const [textValue, setTextValue] = useState(currentLabel);
  const styles = useMultiStyleConfig("Select", {});

  useEffect(() => {
    setTextValue(currentLabel);
  }, [currentLabel]);

  const [innerOptions, setInnerOptions] = useState(options);
  const queryOptions = (query?: string) => {
    if (!options) {
      return;
    }
    const newOptions = options.filter(
      ({ label: optionLabel }) =>
        !query ||
        (searchWithOnlyBeginnings
          ? optionLabel.toLowerCase().startsWith(query.toLowerCase())
          : optionLabel.toLowerCase().includes(query.toLowerCase()))
    );
    setInnerOptions(newOptions);
  };

  const handleSelect = ({ item: option }) => {
    setFieldValue?.(option?.value);
    onChange?.(option?.value);

    if (shouldClearOnSelect) {
      setTextValue("");
      labelRef.current = "";
      return;
    }

    setTextValue(option?.label ?? "");
    labelRef.current = option?.label ?? "";
  };

  const handleInputBlur: FocusEventHandler<HTMLInputElement> = (evt) => {
    onBlur?.(evt);

    /* Delay textValue reset onblur, so that handleSelect could run first. */
    setTimeout(() => setTextValue(labelRef.current), 150);
  };

  const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    if (isReadOnlySelection) {
      return;
    }

    const { value: inputValue } = evt.target;
    const shouldClear = !isNotClearable && !inputValue.length;

    if (shouldClear) {
      setInnerOptions(options);

      labelRef.current = "";
      setTextValue("");

      onChange?.("");
      setFieldValue?.("");
      return;
    }

    queryOptions(inputValue);
    setTextValue(inputValue);
  };

  const handleInputFocus = (evt: FocusEvent<HTMLInputElement>) => {
    /**
     * Always load all available options on focus with an empty query.
     */
    queryOptions("");
    onFocus?.(evt);
  };
  return (
    <Box sx={styles.selectBase}>
      <AutoComplete
        id={id || name}
        openOnFocus
        listAllValuesOnFocus
        onSelectOption={handleSelect}
        emptyState={<NoSuggestions message={noSuggestionsMessage} />}
        freeSolo
        defaultValue={value}
        value={value}
        filter={() => true}
      >
        <SelectInput
          id={id || name}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          value={textValue}
          isReadOnly={isReadOnlySelection}
          {...rest}
        />
        <AutoCompleteList sx={styles.selectList}>
          {innerOptions?.map((option, index) => (
            <AutoCompleteItem
              key={getKey(option.value, index)}
              value={option.value}
              label={option.label}
              sx={styles.selectItem}
            >
              {option.displayLabel || option.label}
            </AutoCompleteItem>
          ))}
        </AutoCompleteList>
      </AutoComplete>
      <FormHint hint={hint} />
    </Box>
  );
};

export default FixedOptionSelect;
